import React from 'react';
import { Box, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import ErrorBoundary from '../Common/ErrorBoundary';
import SalesByMarket from './ProductModel/Charts/SalesByMarket';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid';
import CustomNoRowsOverlay from '../Common/CustomNoRowsOverlay';
import { formatNumber } from '../Common/FormatNumber';
import FadeIn from '../FadeIn';

export default function ProductModel() {
    const [productGrp, setProductGrp] = React.useState('');
    const [productGrps, setProductGrps] = React.useState([]);
    const [kpi, setKpi] = React.useState<any>({});
    const [rows, setRows] = React.useState<any[]>([]);
    const [marketRows, setMarketRows] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [value, setValue] = React.useState(0);
    const [market, setMarket] = React.useState('');
    const [markets, setMarkets] = React.useState([]);
    const marketRef = React.useRef<HTMLDivElement>(null);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (newValue === 0) {
            fetchProductMarketBySku(productGrp);
        }else{
            setMarketRows([]);
            setMarket('');
            fetchMarkets(productGrp);
        }
    };

    React.useEffect(() => {
        const fetchProductGrps = async () => {
            const response = await fetch(`/dashboard/product-groups/?entity_id=${localStorage.getItem('entity_id')}`);
            if (!response.ok) {
                throw new Error('Failed to fetch product groups');
            }
            const data = await response.json();
            setProductGrps(data.product_groups || []);
        };
        fetchProductGrps();

        const handleResize = () => {
            if (marketRef.current) {
                marketRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [productGrp]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProductGrp(event.target.value);
        fetchKpi(event.target.value);
        fetchProductCustomerGroups(event.target.value);
        fetchProductMarketBySku(event.target.value);
    };

    const fetchKpi = async (productGrp: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/dashboard/kpi/?entity_id=${localStorage.getItem('entity_id')}&product_grp=${productGrp}`);
            if (!response.ok) {
                throw new Error('Failed to fetch kpi');
            }
            const data = await response.json();
            setKpi(data.kpi || []);
        } catch (error) {
            console.error('Error fetching kpi:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProductCustomerGroups = async (productGrp: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/dashboard/product-customer-product-group/${productGrp}/?entity_id=${localStorage.getItem('entity_id')}`);
        if (!response.ok) {
                throw new Error('Failed to fetch product customer product group');
            }
            const data = await response.json();
            setRows(data.groups || []);
        } catch (error) {
            console.error('Error fetching product customer groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProductMarketBySku = async (productGrp: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/dashboard/product-market/${productGrp}/?entity_id=${localStorage.getItem('entity_id')}`);
            if (!response.ok) {
                throw new Error('Failed to fetch product market by sku');
            }
            const data = await response.json();
            setMarketRows(data.markets || []);
        } catch (error) {
            console.error('Error fetching product market by sku:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarketChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, productGrp: string) => {
        setMarket(event.target.value);
        setMarketRows([]);
        fetchProductMarketByMarket(productGrp, event.target.value);
    };

    const fetchProductMarketByMarket = async (productGrp: string, market: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/dashboard/product-market-by-market/${productGrp}/${market}/?entity_id=${localStorage.getItem('entity_id')}`);
            if (!response.ok) {
                throw new Error('Failed to fetch product market by market');
            }
            const data = await response.json();
            setMarketRows(data.markets || []);
        } catch (error) {
            console.error('Error fetching product market by market:', error);
        } finally {
            setLoading(false);
            if (marketRef.current) {
                window.scrollTo(0, document.body.scrollHeight);
                // marketRef.current.scrollIntoView({ behavior: 'smooth' });
                // window.scrollTo({ top: marketRef.current.offsetTop, behavior: 'smooth' });
            }
        }
    };

    const fetchMarkets = async (productGrp: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/dashboard/get-all-markets/${productGrp}/?entity_id=${localStorage.getItem('entity_id')}`);
            if (!response.ok) {
                throw new Error('Failed to fetch markets');
            }
            const data = await response.json();
            setMarkets(data.markets || []);
        } catch (error) {
            console.error('Error fetching markets:', error);
        } finally {
            setLoading(false);
        }   
    }

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Cust ID', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'name', headerName: 'Name', width: 200, type: 'string', headerAlign: 'center', align: 'center' },
        { field: 'cust_grp_1', headerName: 'Customer Group', width: 150, type: 'string', headerAlign: 'center', align: 'center',
            renderHeader: (params: GridColumnHeaderParams) => {
                return <span style={{ textAlign: 'center' }}>Customer <br/> Group</span>;
            },
         },
        { field: 'qty', headerName: 'Total Items', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: (params: GridColumnHeaderParams) => {
                return <span style={{ textAlign: 'center' }}>Total <br/> Items</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{new Intl.NumberFormat().format(params.value)}</span>;
            },
         },
        { field: 'sales', headerName: 'Total Sales ($)', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: (params: GridColumnHeaderParams) => {
                return <span style={{ textAlign: 'center' }}>Total <br/> Sales</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        { field: 'gp_pct', headerName: 'Gross Profit (%)', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{params.value.toFixed(2)}%</span>;
            },
        },
    ];

    const marketColumns: GridColDef[] = [
        { field: 'id', headerName: 'Entity Product ID', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'buying_accounts', headerName: 'Buying Accounts', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Buying <br/> Accounts</span>;
            },
         },
        { field: 'pg_annual_invoices', headerName: 'Invoices #', width: 150, type: 'number', headerAlign: 'center', align: 'center' },
        { field: 'pg_annual_sales', headerName: 'Sales ($)', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
         },
        { field: 'pg_gp_pct', headerName: 'Gross Profit (%)', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{params.value.toFixed(2)}%</span>;
            },
        },
        { field: 'avg_trans_size', headerName: 'Avg. Transaction Size', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Avg. <br/>Transaction Size</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        { field: 'average_unit_size', headerName: 'Average Unit Size', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Avg. <br/>Unit Size</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{new Intl.NumberFormat().format(params.value)}</span>;
            },
        },
        {
            field: 'vel_a', headerName: 'Vel. A', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_b', headerName: 'Vel. B', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_c', headerName: 'Vel. C', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_d', headerName: 'Vel. D', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_a', headerName: 'Vol. A', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_b', headerName: 'Vol. B', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_c', headerName: 'Vol. C', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_d', headerName: 'Vol. D', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
    ]

    return (
        <ErrorBoundary fallback = 'Error loading product model'>
            <Box ref={marketRef} sx={{ width: '100%', height: '100%', padding: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', gap: 2, backgroundColor: 'transparent !important' }}>
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.2rem !important', textAlign: 'left', fontFamily: 'Trebuchet MS', fontWeight: 'bold', marginBottom: 2 }}>
                        Please select a product group to view the product model:
                    </Typography>
                    <TextField
                        select
                        size="small"
                        variant="standard"
                        id="product_grp"
                        label="Product Group"
                        value={productGrp}
                        onChange={handleChange}
                        sx={{ width: window.innerWidth > 1200 ? '30%' : '50%' }}
                    >
                        {productGrps.map((productGrp) => (
                            <MenuItem key={productGrp} value={productGrp}>{productGrp}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <FadeIn duration={1000} delay={1000}>
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', gap: 2 }}>
                        {productGrp && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 3 }}>
                                    <FadeIn duration={1000} delay={500}>
                                        <Card elevation={0} sx={{ width: '100%', height: '100%', minHeight: 345, border: localStorage.getItem('theme') === 'dark' ? '1px solid #333' : '1px solid #ddd', borderRadius: 2}}>
                                            <CardHeader title="KPI" slotProps={{ title: { sx: { fontSize: '1rem !important', textAlign: 'left', fontFamily: 'Trebuchet MS' } } }} />
                                            <Divider />
                                            {kpi && Object.keys(kpi).length > 0 ? (
                                                <>
                                                    <CardContent sx={{ width: '100%', height: '100%', fontFamily: 'inherit', margin: 0, padding: 2 }}>
                                                        <Grid container spacing={0} sx={{ width: '100%', height: 'auto'}}>
                                                            {Object.entries(kpi).map(([key, value]) => (
                                                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ marginBottom: 2 }} key={key}>
                                                                    <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'left' }}>{key}</Typography>
                                                                    <Typography variant="body1" sx={{ color: '#000', textAlign: 'left' }}>{value as string}</Typography>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </CardContent>
                                                </>
                                            ) : (
                                                <CardContent sx={{ width: '100%', height: '100%', fontFamily: 'inherit' }}>
                                                    <Typography variant="body1" sx={{ fontSize: '1.2rem !important', textAlign: 'left', fontFamily: 'Trebuchet MS', color: '#bbb' }}>
                                                        No data available
                                                    </Typography>
                                                </CardContent>
                                            )}
                                        </Card>
                                    </FadeIn>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 3, xl: 3 }}>
                                    <FadeIn duration={1000} delay={500}>
                                        <Card elevation={0} sx={{ width: '100%', height: '100%', border: localStorage.getItem('theme') === 'dark' ? '1px solid #333' : '1px solid #ddd', borderRadius: 2}}>
                                            <CardHeader title="Sales By Market" slotProps={{ title: { sx: { fontSize: '1rem !important', textAlign: 'left', fontFamily: 'Trebuchet MS' } } }} />
                                            <Divider />
                                            <CardContent sx={{ width: '100%', height: '100%', fontFamily: 'inherit' }}>
                                                <SalesByMarket product={productGrp} title="" height={345} />
                                            </CardContent>
                                        </Card>
                                    </FadeIn>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                                    <FadeIn duration={1000} delay={500}>
                                        <DataGrid
                                            label='Top Accounts'
                                            loading={loading}
                                            showToolbar
                                            pagination
                                            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                                            pageSizeOptions={[5, 10, 20, 50, 100]}
                                            slots={{
                                                noRowsOverlay: () => <CustomNoRowsOverlay message="No data available" />,
                                            }}
                                            slotProps={{
                                                toolbar: {
                                                    showQuickFilter: true,
                                                    quickFilterProps: { debounceMs: 500 },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiDataGrid-toolbarContainer': {
                                                    color: 'primary.main',
                                                },
                                                '& .MuiDataGrid-toolbarLabel': {
                                                    fontFamily: 'Trebuchet MS',
                                                },
                                                fontFamily: 'inherit',
                                            }}
                                            columns={columns}
                                            rows={rows}
                                        />
                                    </FadeIn>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                    <FadeIn duration={1000} delay={500}>
                                        <Box sx={{ width: '100%' }}>
                                            <Tabs
                                                value={value}
                                                onChange={handleTabChange}
                                                aria-label="Product Model Tabs"
                                            >
                                                <Tab value={0} label="All Markets" sx={{ textTransform: 'capitalize' }} />
                                                <Tab value={1} label="Select a Market" sx={{ textTransform: 'capitalize' }} />
                                            </Tabs>
                                        </Box>
                                        {value === 0 && (
                                            <Box sx={{ width: '100%', height: '100%', marginBottom: 2 }}>
                                                <DataGrid
                                                    loading={loading}
                                                    showToolbar
                                                    columns={marketColumns}
                                                    rows={marketRows}
                                                    pagination
                                                    initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                                                    pageSizeOptions={[5, 10, 20, 50, 100]}                                                
                                                    slots={{
                                                        noRowsOverlay: () => <CustomNoRowsOverlay message="No data available" />,
                                                    }}
                                                    slotProps={{
                                                        toolbar: {
                                                            showQuickFilter: true,
                                                            quickFilterProps: { debounceMs: 500 },
                                                        },
                                                    }} 
                                                    sx={{
                                                        fontFamily: 'inherit',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        {value === 1 && (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', border: localStorage.getItem('theme') === 'dark' ? '1px solid #333' : '1px solid #ddd', borderRadius: 2, padding: 2, gap: 2 }}>
                                                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', gap: 2 }}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        variant="standard"
                                                        id="market"
                                                        label="Market"
                                                        value={market}
                                                        onChange={(event) => handleMarketChange(event, productGrp as string)}
                                                        sx={{ width: window.innerWidth > 1200 ? '30%' : '50%' }}
                                                    >
                                                        {markets.map((market) => (
                                                            <MenuItem key={market} value={market}>{market}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <Divider />
                                                </Box>
                                                {market && marketRows.length > 0 && (
                                                    <FadeIn duration={1000} delay={1000}>
                                                        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'left', gap: 2 }}>
                                                            <DataGrid
                                                                loading={loading}
                                                                showToolbar
                                                                columns={marketColumns}
                                                                rows={marketRows}
                                                                pagination
                                                                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                                                                pageSizeOptions={[5, 10, 20, 50, 100]}
                                                                sx={{
                                                                    fontFamily: 'inherit',
                                                                }}
                                                            />
                                                        </Box>
                                                    </FadeIn>
                                                )}
                                            </Box>
                                        )}
                                    </FadeIn>
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </FadeIn>
            </Box>
        </ErrorBoundary>
    );
}